import BaseEntity from '../../models/BaseEntity'
import { BookingType } from '../../models/OfferBookingType'
import { OfferPrivateType } from '../../models/OfferPrivateType'

/** Matches server enum CustomInputType (BookingCustomInput.cs) */
export enum CustomInputType {
    Unknown,
    Text,
    Number,
    Select,
    Date,
    MultiSelect,
    Boolean,
    File,
    Image,
    Address,
    Time,
    DateTime,
    Country,
    Phone,
    FlightNumber,
    Hotel
}

export enum CustomInputPer {
    PerBooking = 0,
    PerPax = 1,
}

export const CustomInputPerOptions = [
    { value: CustomInputPer.PerBooking, label: 'Per Booking' },
    { value: CustomInputPer.PerPax, label: 'Per Pax' },
]

export interface CustomFieldImportTranslationModel {
    /** Translation ISO code */
    languageCode: string
    fieldName: string
    value: string
}

export interface CustomFieldImportModel {
    fieldName: string
    value: string
    translations?: CustomFieldImportTranslationModel[]
}

export interface TourImportModel extends BaseEntity {
    /** Any external Id (number, uuid etc) */
    externalId?: string
    /** Source last updated date (ISO string from API) */
    lastEditDate?: string
    /** Tour name */
    name?: string
    description?: string
    seoDescription?: string
    termsAndConditions?: string
    /** Map all unknown fields to custom fields */
    customFields?: CustomFieldImportModel[]
    tourItinerary?: string
    locationTiming?: string
    locationAddress?: string
    locationLatitude?: number
    locationLongitude?: number
    externalCurrencyId?: string
    baseCurrencyCode?: string
    customReviewCount?: number
    customReviewsRating?: number
    toursCategories?: number[]
    photos?: string[]
    translations?: TourImportTranslationModel[]
    offers?: OfferImportModel[]
    cityId?: number
    cancellationPolicy?: CancellationPolicyImportModel[]
}

export interface TourImportTranslationModel {
    /** Translation ISO code */
    languageCode: string
    /** Translated name */
    nameDisplay?: string
    description?: string
    seoDescription?: string
    termsAndConditions?: string
    tourItinerary?: string
}

export interface OfferImportModel {
    /** Any external Id (number, uuid etc) */
    externalId?: string
    nameDisplay?: string
    description?: string
    minNumOfPeople?: number
    maxNumOfPeople?: number
    isDirectReporting?: boolean
    customFields?: CustomFieldImportModel[]
    customInputs?: CustomInputImportModel[]
    originIds?: number[]
    translations?: OfferImportTranslationModel[]
    privateType?: OfferPrivateType
    bookingType?: BookingType
    notesToClientsPostBooking?: string
    pickUpMeetingPointInformation?: string
    numberOfHours?: number
    numberOfMinutes?: number
    minimumAdvancedBooking?: number
    childrenAllowed?: boolean
    infantsAllowed?: boolean
    childAge?: number
    infantAge?: number
    utcTimeShift?: number
    timeZone: string
    timeSlots?: TimeSlotImportModel[]
    ticketTypes?: TicketTypeImportModel[]
    pricingPeriods?: OfferPricingPeriodImportModel[]
    cancellationPolicy?: CancellationPolicyImportModel[]
}
export interface TicketTypeImportModel {
    id: string,
    type: string,
    label: string,
    description: string,
    allowed: boolean
}
export interface OfferImportTranslationModel {
    /** Translation ISO code */
    languageCode: string
    nameDisplay?: string
    description?: string
}

export interface TimeSlotImportModel {
    externalId?: string
    startTime?: number
    endTime?: number
}

export interface OfferPricingPeriodImportModel {
    periodStart?: string
    periodEnd?: string | null
    recPricePerAdultBaseCurrency?: number
    recPricePerChildBaseCurrency?: number
    recPricePerTripBaseCurrency?: number
    netPricePerAdultBaseCurrency?: number
    netPricePerChildBaseCurrency?: number
    netPricePerTripBaseCurrency?: number
    retailPricePerAdultBaseCurrency?: number
    retailPricePerChildBaseCurrency?: number
    retailPricePerTripBaseCurrency?: number
    retailPricePerAdultSellingCurrency?: number
    retailPricePerChildSellingCurrency?: number
    retailPricePerTripSellingCurrency?: number
    minimumSellingPricePerAdultBaseCurrency?: number
    minimumSellingPricePerChildBaseCurrency?: number
    minimumSellingPricePerTripBaseCurrency?: number
    minimumSellingPricePerAdultSellingCurrency?: number
    minimumSellingPricePerChildSellingCurrency?: number
    minimumSellingPricePerTripSellingCurrency?: number
}

export interface CustomInputImportModel {
    externalId?: string
    name?: string
    description?: string
    translations?: CustomInputTranslationModel[]
    isRequired?: boolean
    isAddon?: boolean
    regexValidation?: string
    type?: CustomInputType
    per?: CustomInputPer
    isAutocomplete: boolean
    isPaginated: boolean
    /** External API uses bookingScope; mapped to `per` when available */
    bookingScope?: CustomInputPer
    minNumber?: number
    maxNumber?: number
    validFrom?: string
    validTo?: string
    price?: number
    items?: CustomInputSelectOptionModel[]
}

export interface CustomInputTranslationModel {
    /** Translation ISO code */
    languageCode: string
    name?: string
    description?: string
}

export interface CustomInputSelectOptionModel {
    label?: string
    translations?: CustomInputSelectOptionTranslationModel[]
    value?: string
    price?: number
}

export interface CustomInputSelectOptionTranslationModel {
    /** Translation ISO code */
    languageCode: string
    label?: string
}

export interface CancellationPolicyImportModel {
    percents?: number
    hours?: number
}
